/**
 * Mana Locals Admin — CATALOGUE TAXONOMY boundary schemas.
 *
 * CONTRACT SOURCE: the API's M12 catalogue-taxonomy contracts
 * (`m12-catalog-taxonomy/contracts/catalog-taxonomy.schemas.ts`). The category
 * tree and the attribute dictionary are the two records the "Catalogue setup"
 * page creates, and both are written through the ordinary catalogue API.
 *
 * ---------------------------------------------------------------------------
 * THE CATEGORY IS A `categoryId`
 * ---------------------------------------------------------------------------
 * The wire shape names the identifier `categoryId`, NOT `id`, and it is a UUID.
 * The AI's proposal, by contrast, names categories by `id` in `existingCategories`
 * and by `existingCategoryId` in a proposed parent — so the same value is a
 * `categoryId` here and an `id` there. The two vocabularies are kept apart in
 * the names rather than papered over.
 */
import { z } from 'zod';
import {
  CollectionResponseSchema,
  IsoDateTimeSchema,
  ResponseMetaSchema,
  UuidSchema,
} from '@/api/schemas/common';

/** The attribute kinds the dictionary accepts. There is no measure kind. */
export const TaxonomyAttributeKindSchema = z.enum([
  'text',
  'single_select',
  'multi_select',
  'boolean',
  'date',
]);
export type TaxonomyAttributeKind = z.infer<typeof TaxonomyAttributeKindSchema>;

/** The descriptive-vs-variant axis vocabulary, shared with the AI proposals. */
export const TaxonomyVariantAxisSchema = z.enum(['none', 'size', 'colour', 'material']);
export type TaxonomyVariantAxis = z.infer<typeof TaxonomyVariantAxisSchema>;

/** The category's lifecycle state. */
export const CategoryStatusSchema = z.enum(['enabled', 'disabled', 'archived']);
export type CategoryStatus = z.infer<typeof CategoryStatusSchema>;

/** Whether the category is offered to the storefront. */
export const CategoryAvailabilitySchema = z.enum(['available', 'unavailable']);
export type CategoryAvailability = z.infer<typeof CategoryAvailabilitySchema>;

/** The attribute definition's lifecycle state. */
export const AttributeDefinitionStatusSchema = z.enum(['enabled', 'disabled']);
export type AttributeDefinitionStatus = z.infer<typeof AttributeDefinitionStatusSchema>;

/** The SEO block a category carries. */
export const CategorySeoSchema = z.object({
  title: z.string().max(70).nullable(),
  description: z.string().max(160).nullable(),
});

/** A category as the catalogue API returns it. */
export const CategorySchema = z.object({
  categoryId: UuidSchema,
  name: z.string().max(120),
  slug: z.string().min(1).max(160),
  description: z.string().max(2000).nullable(),
  parentCategoryId: UuidSchema.nullable(),
  verticalCode: z.string().nullable(),
  status: CategoryStatusSchema,
  availability: CategoryAvailabilitySchema,
  imageRef: z.string().max(512).nullable(),
  seo: CategorySeoSchema.nullable(),
  sortOrder: z.number().int().min(0).max(9999),
  depth: z.number().int().min(1).max(5),
  templateOrigin: z
    .object({ templateId: UuidSchema, templateVersion: z.number().int().min(1) })
    .nullable(),
  version: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type Category = z.infer<typeof CategorySchema>;

/** `GET /catalog/categories`. */
export const CategoryListResponseSchema = CollectionResponseSchema(CategorySchema);

/** `POST /catalog/categories`. */
export const CategoryResponseSchema = z.object({
  data: CategorySchema,
  meta: ResponseMetaSchema,
});

/**
 * The body that creates a category.
 *
 * `slug` is REQUIRED and lower kebab-case; the API refuses anything else. The
 * body carries no price, no image binary and no status: a new node starts
 * disabled and unavailable, and the API decides that.
 */
export const CreateCategoryRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: z.string().trim().min(1).max(160),
  description: z.string().trim().max(2000).nullable().optional(),
  parentCategoryId: UuidSchema.nullable().optional(),
  verticalCode: z.string().min(1).max(64).optional(),
  imageRef: z.string().min(1).max(512).nullable().optional(),
  seo: CategorySeoSchema.nullable().optional(),
});
export type CreateCategoryRequest = z.infer<typeof CreateCategoryRequestSchema>;

/** The attribute's own validation rules. */
export const AttributeValidationSchema = z.object({
  minLength: z.number().int().min(0).max(10000).nullable().optional(),
  maxLength: z.number().int().min(1).max(10000).nullable().optional(),
  pattern: z.string().nullable().optional(),
  minSelected: z.number().int().min(0).max(100).nullable().optional(),
  maxSelected: z.number().int().min(1).max(100).nullable().optional(),
});

/** One size mapping. */
export const SizeMappingSchema = z.object({
  code: z.string().min(1).max(16),
  label: z.string().min(1).max(40),
});

/** An attribute definition as the dictionary returns it. */
export const AttributeDefinitionSchema = z.object({
  attributeKey: z.string().min(1).max(80),
  name: z.string().max(120),
  kind: TaxonomyAttributeKindSchema,
  options: z.array(z.string().max(120)).max(100).nullable(),
  validation: AttributeValidationSchema.nullable(),
  sizeMappings: z.array(SizeMappingSchema).max(200).nullable(),
  status: AttributeDefinitionStatusSchema,
  usedByCategoryCount: z.number().int().min(0),
  version: z.number().int().min(1),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type AttributeDefinition = z.infer<typeof AttributeDefinitionSchema>;

/** `GET /catalog/attribute-dictionary`. */
export const AttributeDefinitionListResponseSchema =
  CollectionResponseSchema(AttributeDefinitionSchema);

/** `POST /catalog/attribute-dictionary`. */
export const AttributeDefinitionResponseSchema = z.object({
  data: AttributeDefinitionSchema,
  meta: ResponseMetaSchema,
});

/**
 * The body that creates an attribute definition.
 *
 * There is NO `description` member and NO `status` member: the API's create
 * contract carries neither, and a body with either is refused. The console
 * therefore shows a proposed description in the review and does not send it.
 */
export const CreateAttributeDefinitionRequestSchema = z.object({
  attributeKey: z.string().trim().min(1).max(80),
  kind: TaxonomyAttributeKindSchema,
  name: z.string().trim().min(1).max(120),
  options: z.array(z.string().trim().min(1).max(120)).max(100).nullable().optional(),
  validation: AttributeValidationSchema.nullable().optional(),
  sizeMappings: z.array(SizeMappingSchema).max(200).nullable().optional(),
});
export type CreateAttributeDefinitionRequest = z.infer<
  typeof CreateAttributeDefinitionRequestSchema
>;

/**
 * One attribute assignment on a category.
 *
 * `sortOrder` is the API's name for the position; there is no `displayOrder`.
 * `variantAxis` says whether the attribute generates SKUs.
 */
export const CategoryAttributeInputSchema = z.object({
  attributeKey: z.string().min(1).max(80),
  required: z.boolean().default(false),
  variantAxis: TaxonomyVariantAxisSchema.default('none'),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type CategoryAttributeInput = z.infer<typeof CategoryAttributeInputSchema>;

/** The body that replaces a category's attribute set. */
export const SetCategoryAttributesRequestSchema = z.object({
  attributes: z.array(CategoryAttributeInputSchema).max(100),
});
export type SetCategoryAttributesRequest = z.infer<typeof SetCategoryAttributesRequestSchema>;

/** One assignment as the API returns it. */
export const CategoryAttributeSchema = z.object({
  attributeKey: z.string().min(1).max(80),
  required: z.boolean(),
  variantAxis: TaxonomyVariantAxisSchema,
  sortOrder: z.number().int().min(0).max(9999),
});
export type CategoryAttribute = z.infer<typeof CategoryAttributeSchema>;

/**
 * The response of an attribute-set write.
 *
 * `draftVersion` is the category's DRAFT cas token, and it is the value the
 * next write to that category must carry in `If-Match`.
 */
export const CategoryAttributeSetResponseSchema = z.object({
  data: z.object({
    categoryId: UuidSchema,
    attributes: z.array(CategoryAttributeSchema).max(100),
    draftChanged: z.boolean(),
    draftVersion: z.number().int().min(1),
  }),
  meta: ResponseMetaSchema,
});
export type CategoryAttributeSet = z.infer<typeof CategoryAttributeSetResponseSchema>['data'];
